import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Avatar, Button,
  CircularProgress, Alert
} from '@mui/material';
import { Visibility, Search, Download, Refresh, Work, LocationOn, Email, Phone } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/rojgar';

const SeekerManagement = () => {
  const [seekers, setSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileFilter, setProfileFilter] = useState('all');
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [stats, setStats] = useState({ total: 0, complete: 0, incomplete: 0, active: 0 });

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/stats`);
      if (res.data.success) {
        setStats({
          total: res.data.data.totalSeekers || 0,
          complete: 0,
          incomplete: 0,
          active: res.data.data.totalSeekers || 0
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchSeekers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (searchQuery) params.search = searchQuery;
      if (profileFilter !== 'all') params.profileStatus = profileFilter;
      
      const res = await axios.get(`${API_BASE}/admin/seekers`, { params });
      if (res.data.success) {
        setSeekers(res.data.data);
        setTotal(res.data.pagination?.total || res.data.data.length);
        
        // Update stats with profile completion info
        const complete = res.data.data.filter(s => s.isProfileComplete).length;
        setStats(prev => ({
          ...prev,
          complete,
          incomplete: res.data.data.length - complete
        }));
      }
    } catch (err) {
      setError('Error fetching seekers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSeekers();
  }, [page, rowsPerPage, profileFilter]);

  const handleSearch = () => {
    setPage(0);
    fetchSeekers();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
          Rojgar Job Seekers Management
        </Typography>
        <IconButton onClick={() => { fetchStats(); fetchSeekers(); }} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total Seekers', value: stats.total, color: '#2196F3', icon: <Work /> },
          { label: 'Complete Profiles', value: stats.complete, color: '#4CAF50', icon: <Work /> },
          { label: 'Incomplete Profiles', value: stats.incomplete, color: '#FF9800', icon: <Work /> },
          { label: 'Active Seekers', value: stats.active, color: '#9C27B0', icon: <Work /> }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ bgcolor: stat.color, color: 'white' }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                </Box>
                {stat.icon}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, phone or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Profile Status</InputLabel>
              <Select value={profileFilter} label="Profile Status" onChange={(e) => { setProfileFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Profiles</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={handleSearch} fullWidth>Search</Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" startIcon={<Download />} fullWidth>Export</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead sx={{ bgcolor: '#FF5722' }}>
                <TableRow>
                  {['Seeker', 'Contact', 'Location', 'Skills', 'Experience', 'Profile Status', 'Created', 'Actions'].map(header => (
                    <TableCell key={header} sx={{ color: 'white', fontWeight: 'bold' }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {seekers.map((seeker) => (
                  <TableRow key={seeker._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: '#FF5722' }} src={seeker.profileImage}>
                          {(seeker.fullName || seeker.name || 'S')[0]}
                        </Avatar>
                        <Box>
                          <Typography fontWeight="bold">{seeker.fullName || seeker.name || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">{seeker.currentRole || 'No role'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2"><Phone sx={{ fontSize: 14, mr: 0.5 }} />{seeker.mobile || 'N/A'}</Typography>
                        <Typography variant="caption"><Email sx={{ fontSize: 12, mr: 0.5 }} />{seeker.email || 'N/A'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ fontSize: 14, mr: 0.5, color: 'gray' }} />
                        {seeker.city || 'N/A'}, {seeker.state || ''}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(seeker.skills || []).slice(0, 2).map((skill, i) => (
                          <Chip key={i} label={skill} size="small" variant="outlined" />
                        ))}
                        {(seeker.skills || []).length > 2 && (
                          <Chip label={`+${seeker.skills.length - 2}`} size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{seeker.experience || 0} years</TableCell>
                    <TableCell>
                      <Chip 
                        label={seeker.isProfileComplete ? 'Complete' : 'Incomplete'} 
                        color={seeker.isProfileComplete ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{seeker.createdAt ? new Date(seeker.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedSeeker(seeker); setDetailDialog(true); }}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {seekers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No seekers found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF5722', color: 'white' }}>
          Seeker Profile - {selectedSeeker?.fullName || selectedSeeker?.name}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedSeeker && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 1, bgcolor: '#FF5722' }} src={selectedSeeker.profileImage}>
                    {(selectedSeeker.fullName || selectedSeeker.name || 'S')[0]}
                  </Avatar>
                  <Typography variant="h6">{selectedSeeker.fullName || selectedSeeker.name}</Typography>
                  <Typography color="textSecondary">{selectedSeeker.currentRole || 'Job Seeker'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography><b>Phone:</b> {selectedSeeker.mobile || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Email:</b> {selectedSeeker.email || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>City:</b> {selectedSeeker.city || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>State:</b> {selectedSeeker.state || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Experience:</b> {selectedSeeker.experience || 0} years</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Education:</b> {selectedSeeker.education || 'N/A'}</Typography></Grid>
                  <Grid item xs={12}>
                    <Typography><b>Skills:</b></Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {(selectedSeeker.skills || []).map((skill, i) => (
                        <Chip key={i} label={skill} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={6}><Typography><b>Expected Salary:</b> ₹{selectedSeeker.expectedSalaryMin || 0} - ₹{selectedSeeker.expectedSalaryMax || 0}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Job Type:</b> {selectedSeeker.preferredJobType || 'Any'}</Typography></Grid>
                  <Grid item xs={12}>
                    <Typography><b>Bio:</b> {selectedSeeker.bio || 'No bio provided'}</Typography>
                  </Grid>
                  {selectedSeeker.resumeUrl && (
                    <Grid item xs={12}>
                      <Button variant="outlined" href={selectedSeeker.resumeUrl} target="_blank">View Resume</Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeekerManagement;
